export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  score: number; // 0-100, 100 being strongest
}

export interface PasswordValidationRules {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  blockedPatterns?: string[];
}

// Enterprise-level password validation based on 2024 NIST SP 800-63 and PCI DSS 4.0 standards
export const enterprisePasswordRules: PasswordValidationRules = {
  minLength: 12, // 12 for everyone (#1724 4.5); goapi refuses shorter (password_policy.go)
  requireUppercase: false,
  requireLowercase: false,
  requireNumbers: false,
  requireSpecialChars: false,
  blockedPatterns: [
    // Common patterns to avoid
    'password', 'admin', 'user', 'login', 'platinum',
    '123456', 'qwerty', 'abc123', 'letmein', 'welcome',
    // Sequential patterns
    'abcd', '1234', 'qwer'
  ]
}

export const validatePassword = (
  password: string, 
  rules: PasswordValidationRules = enterprisePasswordRules
): PasswordValidationResult => {
  const errors: string[] = []
  let score = 0

  // Check minimum length
  if (password.length < rules.minLength) {
    errors.push(`Password must be at least ${rules.minLength} characters long`)
  } else {
    score += 20 // Base score for meeting minimum length
    // Bonus points for extra length
    if (password.length >= 15) score += 10
    if (password.length >= 20) score += 10
  }

  // Check for uppercase letters
  if (rules.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter')
  } else if (rules.requireUppercase) {
    score += 15
  }

  // Check for lowercase letters
  if (rules.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter')
  } else if (rules.requireLowercase) {
    score += 15
  }

  // Check for numbers
  if (rules.requireNumbers && !/\d/.test(password)) {
    errors.push('Password must contain at least one number')
  } else if (rules.requireNumbers) {
    score += 15
  }

  // Check for special characters
  if (rules.requireSpecialChars && !/[^a-zA-Z0-9]/.test(password)) {
    errors.push('Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)')
  } else if (rules.requireSpecialChars) {
    score += 15
  }

  // Check for blocked patterns
  if (rules.blockedPatterns) {
    const lowercasePassword = password.toLowerCase()
    for (const pattern of rules.blockedPatterns) {
      if (lowercasePassword.includes(pattern.toLowerCase())) {
        errors.push(`Password cannot contain common patterns like "${pattern}"`)
        score -= 10 // Penalty for common patterns
        break
      }
    }
  }

  // Check for repetitive characters (more than 3 consecutive identical characters)
  if (/(.)\1{3,}/.test(password)) {
    errors.push('Password cannot contain more than 3 consecutive identical characters')
    score -= 5
  }

  // Check for keyboard patterns
  const keyboardPatterns = ['qwerty', 'asdf', 'zxcv', '1234', '4321']
  const lowercasePassword = password.toLowerCase()
  for (const pattern of keyboardPatterns) {
    if (lowercasePassword.includes(pattern)) {
      errors.push('Password cannot contain keyboard patterns')
      score -= 10
      break
    }
  }

  // Bonus points for character diversity
  const uniqueChars = new Set(password).size
  if (uniqueChars >= password.length * 0.6) {
    score += 10 // Good character diversity
  }

  // Bonus for mixing character types effectively
  const hasMultipleUppercase = (password.match(/[A-Z]/g) || []).length >= 2
  const hasMultipleLowercase = (password.match(/[a-z]/g) || []).length >= 2
  const hasMultipleNumbers = (password.match(/\d/g) || []).length >= 2
  const hasMultipleSpecial = (password.match(/[^a-zA-Z0-9]/g) || []).length >= 2

  if (hasMultipleUppercase && hasMultipleLowercase && hasMultipleNumbers && hasMultipleSpecial) {
    score += 10
  }

  // Ensure score doesn't exceed 100 or go below 0
  score = Math.max(0, Math.min(100, score))

  return {
    isValid: errors.length === 0,
    errors,
    score
  }
}

export const validatePasswordMatch = (password: string, confirmPassword: string): boolean => {
  return password === confirmPassword && password.length > 0
}

export const getPasswordStrengthLabel = (score: number): string => {
  if (score >= 90) return 'Excellent'
  if (score >= 75) return 'Strong'
  if (score >= 60) return 'Good'
  if (score >= 40) return 'Fair'
  if (score >= 20) return 'Weak'
  return 'Very Weak'
}

export const getPasswordStrengthColor = (score: number): string => {
  if (score >= 90) return '#4caf50' // Green
  if (score >= 75) return '#8bc34a' // Light green
  if (score >= 60) return '#ffeb3b' // Yellow
  if (score >= 40) return '#ff9800' // Orange
  if (score >= 20) return '#ff5722' // Red-orange
  return '#f44336' // Red
} 